import React, { useState, useEffect, useCallback } from "react";
import styles from "./CompanyList.module.sass";
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../components/Icon";
import { useNavigate } from "react-router-dom"; 
import { getCompanies, getCompaniesWithContractCounts } from "../../services/company.service";

const CompanyList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleStatusChange = (id, newStatus) => {
    const updatedCompanies = companies.map(company => 
      company.id === id ? { ...company, status: newStatus } : company
    );
    setCompanies(updatedCompanies);
  };

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      const filterString = search.trim() 
        ? `(name~'${search.toLowerCase().trim()}' || sector~'${search.toLowerCase().trim()}')` 
        : null;

      const result = await getCompanies({
        page,
        perPage: 10,
        filter: filterString 
      });
      
      setCompanies(result.items || []); 
      setTotalPages(result.totalPages || 1); 
      setNoResults(result.items.length === 0 && !!search);

    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des entreprises:", err);
        setError("Impossible de charger les entreprises");
        setCompanies([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search]); 
  
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1); 
    fetchCompanies();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Card 
        className={styles.card} 
        title="Entreprises" 
        classTitle={cn("title-blue", styles.title)} 
        classCardHead={styles.head} 
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={setSearch}
              onSubmit={handleSearch}
              placeholder="Rechercher par nom ou secteur"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/companies/create")}>Créer une entreprise</button>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <div className={styles.row}>
            {noResults && companies.length === 0 && (
              <div className={styles.empty}>
                Aucune entreprise trouvée pour votre recherche "{search}".
              </div>
            )}
            {!noResults && companies.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucune entreprise disponible.
              </div>
            )}
            {companies.length > 0 && (
              <>
                <Table 
                  className={styles.table} 
                  items={companies} 
                  onStatusChange={handleStatusChange}
                  onSelectionChange={setSelectedIds}
                />

                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, page - 1))} 
                    disabled={page === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {page} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
                    disabled={page === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
      <Panel 
        selectedIds={selectedIds} 
        onDeleteSuccess={() => {
          setSelectedIds([]);
          fetchCompanies();
        }}
      />
    </>
  );
};

export default CompanyList;